import { Stack, Typography } from '@montugroup/design-system';
import type { ConsultationResponseDto, ConsultationWaitlistDto } from '@montugroup/pms-api-contracts';
import { useState } from 'react';

import { AddMobilePrompt } from './AddMobilePrompt';
import { WAITLIST_CHANGE_REFUSED_MESSAGE, WAITLIST_NOT_ELIGIBLE_MESSAGE } from './waitlist.config';
import { WaitlistJoinPrompt } from './WaitlistJoinPrompt';
import { WaitlistMembership } from './WaitlistMembership';

interface ConsultationWaitlistSectionProps {
  consultation: ConsultationResponseDto;
  /** called with what happened after a join, change or leave; the card announces it and takes focus */
  onOutcome: (message: string) => void;
}

type WaitlistStatus = 'onWaitlist' | 'addMobile' | 'canJoin';

// kept here, not in the component that was refused, as the refetch can unmount that component
type WaitlistRefusal =
  // the waitlist data the join was refused against; react-query keeps the same object when the refetch returns the
  // same data, so the refusal replaces the join prompt until the server's state actually changes
  | { kind: 'join'; against: ConsultationWaitlistDto }
  // a refused change leaves the patient on the waitlist, so it's explained above that state, not instead
  | { kind: 'change' };

const waitlistStatus = (waitlist: ConsultationWaitlistDto): WaitlistStatus | null => {
  // a patient already on the waitlist can still leave once the consultation is ineligible, unless the doctor has been
  // taken off the waitlist
  if (waitlist.status === 'ACTIVE' && waitlist.ineligibleReason !== 'DISABLED') {
    return 'onWaitlist';
  }
  if (waitlist.ineligibleReason === 'NO_MOBILE') {
    return 'addMobile';
  }
  // pms-service turns the waitlist on per doctor; DISABLED means this doctor isn't on it, so show nothing
  return waitlist.eligible ? 'canJoin' : null;
};

const refusalMessage = (refusal: WaitlistRefusal): string =>
  refusal.kind === 'join' ? WAITLIST_NOT_ELIGIBLE_MESSAGE : WAITLIST_CHANGE_REFUSED_MESSAGE;

export function ConsultationWaitlistSection({ consultation, onOutcome }: ConsultationWaitlistSectionProps) {
  const { waitlist } = consultation;
  const [refusal, setRefusal] = useState<WaitlistRefusal | null>(null);

  if (!waitlist) {
    return null;
  }

  const status = waitlistStatus(waitlist);
  const clearRefusal = () => setRefusal(null);

  // join() resolves once the refetch is in the cache, but the section only gets it through the page's next render, so
  // until then it still has the data that offered the join, and the refusal replaces that prompt
  const isJoinRefusedForThisData = refusal?.kind === 'join' && refusal.against === waitlist;
  const hasContent = status !== null && !isJoinRefusedForThisData;
  // a refused change is explained above the state; a refused join only once nothing else shows
  const message = refusal && (refusal.kind === 'change' || !hasContent) ? refusalMessage(refusal) : null;

  if (!hasContent && !message) {
    return null;
  }

  return (
    <Stack width="100%" gap={2} data-testid="consultation-waitlist-section">
      {message && <Typography variant="body1">{message}</Typography>}
      {status === 'onWaitlist' && (
        <WaitlistMembership
          consultation={consultation}
          waitlist={waitlist}
          onOutcome={onOutcome}
          onRefused={() => {
            setRefusal({ kind: 'change' });
            onOutcome(WAITLIST_CHANGE_REFUSED_MESSAGE);
          }}
          onAction={clearRefusal}
        />
      )}
      {status === 'addMobile' && <AddMobilePrompt>to join the waitlist for an earlier time.</AddMobilePrompt>}
      {status === 'canJoin' && !isJoinRefusedForThisData && (
        <WaitlistJoinPrompt
          consultation={consultation}
          waitlist={waitlist}
          onOutcome={onOutcome}
          onRefused={() => {
            setRefusal({ kind: 'join', against: waitlist });
            onOutcome(WAITLIST_NOT_ELIGIBLE_MESSAGE);
          }}
          onAction={clearRefusal}
        />
      )}
    </Stack>
  );
}
